import React, { useEffect, useState } from "react";
import { MessageSquare, Trash2, Eye } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import AdminDetailDialog, {
  AdminDetailField,
  AdminDetailSection,
} from "../../components/admin/AdminDetailDialog";
import { toast } from "sonner";
import { ADMIN_CHIP, ADMIN_CHIP_ACTIVE, ADMIN_LABEL } from "../../config/brand";

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const response = await authFetch("/api/admin/contacts");
      if (response.ok) {
        setContacts(await response.json());
      } else {
        toast.error("Impossible de charger les contacts");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = (contact) => {
    if (!contact?.unread) return;
    setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, unread: false } : c)));
    authFetch(`/api/admin/contacts/${contact.id}/read`, { method: "POST" }).catch(() => {});
  };

  const openDetail = (contact) => {
    setDetail(contact);
    markRead(contact);
  };

  const unreadCount = contacts.filter((c) => c.unread).length;
  const visible = filter === "unread" ? contacts.filter((c) => c.unread) : contacts;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/contacts/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
      toast.success(`Message de ${deleteTarget.name} supprimé`);
      setDeleteTarget(null);
      setContacts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch {
      toast.error("Impossible de supprimer");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        icon={MessageSquare}
        title="Contacts"
        description={`${contacts.length} message(s) reçu(s) via le site`}
      />

      <div className="flex gap-2 mb-6 flex-wrap" role="group" aria-label="Filtrer les contacts">
        <button
          type="button"
          onClick={() => setFilter("all")}
          aria-pressed={filter === "all"}
          className={filter === "all" ? ADMIN_CHIP_ACTIVE : ADMIN_CHIP}
        >
          Tous ({contacts.length})
        </button>
        <button
          type="button"
          onClick={() => setFilter("unread")}
          aria-pressed={filter === "unread"}
          className={filter === "unread" ? ADMIN_CHIP_ACTIVE : ADMIN_CHIP}
        >
          Non lus ({unreadCount})
        </button>
      </div>

      {loading ? (
        <AdminLoading message="Chargement des contacts..." />
      ) : visible.length === 0 ? (
        <AdminEmptyState
          icon={MessageSquare}
          title={filter === "unread" ? "Aucun message non lu" : "Aucun message"}
          description={
            filter === "unread"
              ? "Tous les messages ont été ouverts."
              : "Les messages envoyés via le site apparaîtront ici."
          }
        />
      ) : (
        <AdminTable
          mobileCards={visible.map((contact) => (
            <AdminMobileCard
              key={contact.id}
              title={contact.name}
              subtitle={contact.email}
              onClick={() => openDetail(contact)}
              meta={[
                { label: "Téléphone", value: contact.phone },
                { label: "Date", value: formatDate(contact.created_at) },
                { label: "Message", value: contact.message },
              ]}
              actions={
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#3a00e0] hover:text-[#4400e8]"
                    onClick={() => openDetail(contact)}
                    aria-label={`Voir le message de ${contact.name}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Voir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeleteTarget(contact)}
                    aria-label={`Supprimer le message de ${contact.name}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Nom</TableHead>
                <TableHead className={adminTableHeadClass}>Email</TableHead>
                <TableHead className={adminTableHeadClass}>Téléphone</TableHead>
                <TableHead className={adminTableHeadClass}>Message</TableHead>
                <TableHead className={adminTableHeadClass}>Date</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((contact) => (
                <TableRow
                  key={contact.id}
                  className={adminClickableRowClass}
                  onClick={() => openDetail(contact)}
                >
                  <TableCell className="text-slate-900 font-medium">
                    <span className="inline-flex items-center gap-2">
                      {contact.unread ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3a00e0] shrink-0" aria-label="Non lu" />
                      ) : null}
                      {contact.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-[#3a00e0] hover:text-[#4400e8] transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {contact.email}
                    </a>
                  </TableCell>
                  <TableCell className={ADMIN_LABEL}>{contact.phone || "—"}</TableCell>
                  <TableCell className="text-slate-700 max-w-xs">
                    <span className="line-clamp-2" title={contact.message}>
                      {contact.message}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm whitespace-nowrap">
                    {formatDate(contact.created_at)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#3a00e0] hover:text-[#4400e8]"
                        onClick={() => openDetail(contact)}
                        aria-label={`Voir le message de ${contact.name}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteTarget(contact)}
                        aria-label={`Supprimer le message de ${contact.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <AdminDetailDialog
        open={!!detail}
        onOpenChange={(open) => !open && setDetail(null)}
        title={detail?.name || "Contact"}
        footer={
          detail ? (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 sm:mr-auto"
              onClick={() => {
                setDeleteTarget(detail);
                setDetail(null);
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Supprimer
            </Button>
          ) : null
        }
      >
        {detail ? (
          <>
            <AdminDetailSection title="Coordonnées">
              <AdminDetailField label="Nom">{detail.name}</AdminDetailField>
              <AdminDetailField label="Email">
                {detail.email ? (
                  <a href={`mailto:${detail.email}`} className="text-[#3a00e0] hover:underline">
                    {detail.email}
                  </a>
                ) : null}
              </AdminDetailField>
              <AdminDetailField label="Téléphone">
                {detail.phone ? (
                  <a href={`tel:${detail.phone}`} className="text-[#3a00e0] hover:underline">
                    {detail.phone}
                  </a>
                ) : null}
              </AdminDetailField>
              <AdminDetailField label="Date">{formatDate(detail.created_at)}</AdminDetailField>
              {detail.tag ? <AdminDetailField label="Origine">{detail.tag}</AdminDetailField> : null}
              <AdminDetailField label="Identifiant">{detail.id}</AdminDetailField>
            </AdminDetailSection>
            <AdminDetailSection title="Message" cols={1}>
              <AdminDetailField label="Message">{detail.message}</AdminDetailField>
            </AdminDetailSection>
          </>
        ) : null}
      </AdminDetailDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={`Supprimer le message de ${deleteTarget?.name || ""} ?`}
        description={`Le message de ${deleteTarget?.name || "ce contact"} sera définitivement supprimé.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
